import { ExportOutlined } from '@ant-design/icons';
import { OrderDoc, OrderProduct, OrderStatusCode, ProductStateCode } from '@gooduncles/gu-app-schema';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { ColDef, ColGroupDef, ICellRendererParams, RowClassParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Button, Tabs, message } from 'antd';
import { groupBy, uniq } from 'lodash-es';
import { FC, useCallback, useMemo, useState } from 'react';

import { formatDateForAgCell } from 'src/utils/ag-grid-util';
import { formatNumber } from 'src/utils/common';
import { formatDate } from 'src/utils/date-util';
import { productVolumeChangeValidator } from 'src/utils/order-util';

import { useOrderProductIssueModal } from 'src/stores/useOrderProductIssueModal';

import useOrderListForUser from 'src/hooks/swr/useOrderListForUser';

import DownloadRawDataCell from 'src/atomic-components/atoms/AgCell/AgCellDownloadRawData';
import AgGroupCellMonthlyOrder from 'src/atomic-components/atoms/AgCell/AgGroupCellMonthlyOrder/AgGroupCellMonthlyOrder';
import OrderAndLogViewModal from 'src/atomic-components/organisms/OrderAndLogView/OrderAndLogViewModal';
import IssuePanelModal from 'src/components/Issue/IssuePanelModal';
import SoldOutCellRenderer from 'src/components/Order/OrderTable/SoldOutCellRenderer/SoldOutCellRenderer';

import classes from './UserOrderList.module.scss';

type ProductWithOrder = OrderProduct & {
  order: OrderDoc;
};

type UserOrderListProps = {
  userId: string;
};

const getDetailRowStyle = (params: RowClassParams<ProductWithOrder>) => {
  if (params.data?.state === ProductStateCode.OUTOFSTOCK) {
    return { backgroundColor: 'hsl(357, 100%, 95%)' };
  }
};

const items = (groupedOrders: Record<string, OrderDoc[]>) => {
  return [
    {
      key: 'submitted',
      label: '주문완료',
      children: (
        <UserOrderTable
          orders={groupedOrders[OrderStatusCode.SUBMITTED] ?? []}
          orderStatus={OrderStatusCode.SUBMITTED}
        />
      ),
    },
    {
      key: 'accepted',
      label: '수락완료',
      children: (
        <UserOrderTable orders={groupedOrders[OrderStatusCode.ACCEPTED] ?? []} orderStatus={OrderStatusCode.ACCEPTED} />
      ),
    },
    {
      key: 'delivered',
      label: '배송완료',
      children: (
        <UserOrderTable
          orders={groupedOrders[OrderStatusCode.DELIVERED] ?? []}
          orderStatus={OrderStatusCode.DELIVERED}
          showRowGroup
        />
      ),
    },
    {
      key: 'canceled',
      label: '취소(사용자)',
      children: (
        <UserOrderTable orders={groupedOrders[OrderStatusCode.CANCELED] ?? []} orderStatus={OrderStatusCode.CANCELED} />
      ),
    },
    {
      key: 'rejected',
      label: '취소(관리자)',
      children: (
        <UserOrderTable orders={groupedOrders[OrderStatusCode.REJECTED] ?? []} orderStatus={OrderStatusCode.REJECTED} />
      ),
    },
  ];
};

const UserOrderList: FC<UserOrderListProps> = ({ userId }) => {
  const { orderList } = useOrderListForUser(userId);
  const groupByStatus = groupBy(orderList, 'orderStatus');
  const deliveredOrders = groupByStatus[OrderStatusCode.DELIVERED] ?? [];
  const totalOrderCount = deliveredOrders.length;
  const totalAmount = deliveredOrders.reduce((acc, order) => acc + order.totalAmount, 0);
  const avgUnit = Math.floor(totalAmount / totalOrderCount);
  const groupByMonth = groupBy(
    deliveredOrders.map((order) => ({ ...order, month: formatDate(order.orderDate, 'y년 L월') })),
    'month'
  );
  const avgMonthlyAmount = Math.floor(totalAmount / Object.keys(groupByMonth).length);
  const productVarietyCount = uniq(deliveredOrders.flatMap((order) => order.products.map((p) => p.productId))).length;

  return (
    <div className={classes.userOrderListContainer}>
      <table className={classes.summary}>
        <tr>
          <td>총 주문</td>
          <td>
            <span>{totalOrderCount}</span>건 (배송완료 기준)
          </td>
          <td>총 주문금액</td>
          <td>
            <span>{formatNumber(totalAmount)}</span>원
          </td>
        </tr>
        <tr>
          <td>평균 단가</td>
          <td>
            <span>{formatNumber(avgUnit)}</span>원
          </td>
          <td>월 평균 주문금액</td>
          <td>
            <span>{formatNumber(avgMonthlyAmount)}</span>원
          </td>
        </tr>
        <tr>
          <td />
          <td />
          <td>총 주문 품목수</td>
          <td>
            <span>{formatNumber(productVarietyCount)}</span>개
          </td>
        </tr>
      </table>

      <Tabs defaultActiveKey='delivered' items={items(groupByStatus)} style={{ flex: 1 }} />
    </div>
  );
};

const defaultColDef: ColDef<OrderDoc> = {
  flex: 1,
  sortable: true,
  resizable: true,
  filter: true,
};

const groupRowRendererParams = {
  suppressCount: true,
  innerRenderer: AgGroupCellMonthlyOrder,
};

const columnTypes = {
  number: {
    aggFunc: 'sum',
  },
};

const UserOrderTable: FC<{ orders: OrderDoc[]; orderStatus: OrderStatusCode; showRowGroup?: boolean }> = ({
  orders,
  orderStatus,
  showRowGroup,
}) => {
  const [, setGridRef] = useState<AgGridReact<OrderDoc> | null>(null);
  const [issuePanelModalOpen, setIssuePanelModalOpen] = useState(false);
  const [issuePanelOrderId, setIssuePanelOrderId] = useState<string | null>(null);

  const handleOpenIssuePanel = useCallback((orderId: string) => {
    setIssuePanelOrderId(orderId);
    setIssuePanelModalOpen(true);
  }, []);

  const handleCloseIssuePanel = useCallback(() => {
    setIssuePanelModalOpen(false);
    setIssuePanelOrderId(null);
  }, []);

  const IssuePanelButtonCellRenderer = useCallback(
    (params: ICellRendererParams<OrderDoc>) => {
      const orderId = params.data?._id;
      if (!orderId) return null;

      return (
        <Button
          type='link'
          size='small'
          iconPosition='end'
          icon={<ExportOutlined />}
          onClick={() => handleOpenIssuePanel(orderId)}>
          이슈
        </Button>
      );
    },
    [handleOpenIssuePanel]
  );

  const columnDefs: (ColDef<OrderDoc> | ColGroupDef<OrderDoc>)[] = useMemo(
    () => [
      { headerName: 'OrderId', field: '_id', minWidth: 300, hide: true },
      {
        headerName: '_timeCreate',
        field: '_timeCreate',
        minWidth: 300,
        valueFormatter: formatDateForAgCell('yyyy-MM-dd HH:mm:ss'),
        hide: true,
      },
      {
        headerName: '월',
        field: 'orderDate',
        minWidth: 160,
        rowGroup: showRowGroup,
        hide: true,
        valueGetter: (params) => {
          if (!params.data) return '';
          if (params.data?.orderStatus === OrderStatusCode.DELIVERED) {
            const dateValue = params.data.deliveryTaskFinishedAt ?? params.data.date ?? params.data.deliveryDate;
            if (!dateValue) return '🚨 오류';
            return formatDate(dateValue, 'y년 L월');
          }
          // 미배송된 주문은 배송 완료시각이 없기 때문에 주문일을 기준으로 그룹화한다.
          return params.data.orderDate ? formatDate(params.data.orderDate, 'y년 L월') : '';
        },
      },
      { field: 'totalAmount', aggFunc: 'sum', valueFormatter: ({ value }) => formatNumber(value), hide: true },
      {
        headerName: '주문시각',
        field: 'orderDate',
        colId: 'orderDateField',
        minWidth: 160,
        valueFormatter: ({ value }) => (value ? formatDate(value, 'L월 d일(EEEEEE) HH:mm:ss') : ''),
      },
      {
        headerName: '배송시각',
        field: 'deliveryTaskFinishedAt',
        colId: 'deliveryTaskFinishedAtField',
        minWidth: 160,
        valueFormatter: ({ value }) => (value ? formatDate(value, 'L월 d일(EEEEEE) HH:mm:ss') : ''),
      },
      {
        headerName: '주문금액(배송비 포함)',
        field: 'grandTotal',
        minWidth: 140,
        cellRenderer: 'agGroupCellRenderer',
        valueGetter: (params) => params.data?.grandTotal ?? params.data?.paidAmount,
        valueFormatter: ({ value }) => formatNumber(value),
      },
      { headerName: '품목수', minWidth: 88, valueGetter: (params) => params.data?.products.length ?? '' },
      {
        headerName: '가격동기화',
        field: 'products',
        pinned: 'right',
        maxWidth: 120,
        valueFormatter: ({ value, data }) => {
          if (data?._id === 'summary' || !data?._id) return (data as any)?.isAllSynced ? '✅' : '⚪️';
          const products = value as OrderDoc['products'];
          const isAllSynced = products.every((product) => product.priceDiff !== null);
          return products.length > 0 ? (isAllSynced ? '✅' : '⚪️') : '';
        },
      },
      {
        headerName: '배송 담당자',
        field: 'deliveryPartnerName',
      },
      {
        field: '_id',
        width: 130,
        headerName: '주문 상세',
        cellRenderer: (params: any) => {
          const value = params.value;
          return <OrderAndLogViewModal orderId={value} />;
        },
      },
      {
        headerName: '이슈',
        width: 100,
        cellRenderer: IssuePanelButtonCellRenderer,
        filter: false,
        sortable: false,
        hide: ![OrderStatusCode.ACCEPTED, OrderStatusCode.DELIVERED].includes(orderStatus),
      },
      {
        headerName: '액션',
        minWidth: 120,
        cellRenderer: DownloadRawDataCell,
        hide: true,
      },
    ],
    [showRowGroup, orderStatus, IssuePanelButtonCellRenderer]
  );

  const detailCellRendererParams = useMemo(() => {
    const detailColumnDefs: (ColDef<ProductWithOrder> | ColGroupDef<ProductWithOrder>)[] = [
      {
        headerName: '상품코드',
        field: 'productId',
        minWidth: 96,
      },
      {
        headerName: '상품명',
        field: 'fullName',
        minWidth: 180,
      },
      {
        headerName: '가격',
        field: 'snapshotPrice',
        minWidth: 100,
        maxWidth: 160,
        valueFormatter: (params) => {
          const { value } = params;
          const { price } = params.data as ProductWithOrder;
          return formatNumber(value ?? price ?? 0);
        },
      },
      {
        headerName: '주문 후 가격 변동',
        field: 'priceDiff',
        minWidth: 100,
        maxWidth: 160,
        valueFormatter: (params) => `${params.value > 0 ? '+' : ''}${formatNumber(params.value ?? 0)}`,
      },
      {
        headerName: '*수량',
        field: 'volume',
        minWidth: 100,
        maxWidth: 160,
        editable: true,
        valueFormatter: (params) => formatNumber(params.value),
        valueSetter: (params) => {
          const newValue = Number(params.newValue);
          if (isNaN(newValue)) {
            message.error('숫자만 입력 가능합니다.');
            return false;
          }

          // 값이 변경되지 않았으면 무시
          if (params.oldValue === newValue) {
            return false;
          }

          if (!productVolumeChangeValidator(params.data)) {
            return false;
          }

          // Zustand 스토어를 통해 모달 열기
          const order = params.data.order;
          useOrderProductIssueModal.getState().open({
            order,
            store: {
              storeId: order.storeId,
              storeName: '',
            },
            product: params.data,
            changeType: 'volume_change',
            newVolume: newValue,
            source: '사용자 관리',
          });

          // 값 변경은 모달 완료 후 수행하므로 false 반환
          return false;
        },
      },
      {
        headerName: '총액',
        maxWidth: 160,
        valueFormatter: (params) => {
          const { price, snapshotPrice } = params.data as ProductWithOrder;
          return formatNumber((snapshotPrice ?? price ?? 0) * (params.data?.volume ?? 0));
        },
      },
      {
        headerName: '품절 처리',
        maxWidth: 160,
        cellRenderer: SoldOutCellRenderer,
      },
    ];

    return {
      refreshStrategy: 'rows',
      detailGridOptions: {
        columnDefs: detailColumnDefs,
        defaultColDef,
        enableCellChangeFlash: true,
        onFirstDataRendered: (params: any) => {
          params.api.sizeColumnsToFit();
        },
        getRowStyle: getDetailRowStyle,
        context: {
          issueModalSource: '사용자 관리',
        },
      },
      getDetailRowData: (params: any) => {
        const order = params.data;
        const data = order?.products.map((product: any) => ({ ...product, order }));
        params.successCallback(data);
      },
    };
  }, []);

  const isRowMaster = useMemo(() => {
    return (dataItem: OrderDoc) => {
      return dataItem ? dataItem.products && dataItem.products.length > 0 : false;
    };
  }, []);

  return (
    <>
      <div className='ag-theme-alpine' style={{ height: '100%' }}>
        <AgGridReact
          ref={setGridRef}
          rowData={orders}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          suppressScrollOnNewData={true}
          alwaysShowHorizontalScroll={true}
          isRowMaster={isRowMaster}
          keepDetailRows={true}
          masterDetail={true}
          detailRowAutoHeight={true}
          enableCellChangeFlash={true}
          detailCellRendererParams={detailCellRendererParams}
          // rows group
          groupDisplayType={'groupRows'}
          groupRowRendererParams={groupRowRendererParams}
          columnTypes={columnTypes}
        />
      </div>
      {issuePanelOrderId && (
        <IssuePanelModal open={issuePanelModalOpen} onClose={handleCloseIssuePanel} orderId={issuePanelOrderId} />
      )}
    </>
  );
};

export default UserOrderList;
